// API module for ChatWidget - handles all fetch calls and error handling
import { t } from './i18n.js';
import { showError } from './ui.js';
import { saveSessionId, loadMessages } from './state.js';

// Module-level property ID for X-Property-ID header
let currentPropertyID = null;
let isPreviewMode = false;

// Set the property ID for all API calls (called when property is resolved)
export function setAPIPropertyID(propertyID) {
    currentPropertyID = propertyID;
}

// Set preview mode flag (admin panel preview widget)
export function setPreviewMode(preview) {
    isPreviewMode = preview;
}

// Build headers object with optional X-Property-ID and X-Preview-Mode
function buildHeaders() {
    const headers = {
        'Content-Type': 'application/json'
    };
    if (currentPropertyID) {
        headers['X-Property-ID'] = currentPropertyID;
    }
    if (isPreviewMode) {
        headers['X-Preview-Mode'] = 'true';
    }
    return headers;
}

// Handle API error responses with i18n support
export async function handleAPIError(response, messagesContainer, language, fallbackMessageCode = 'GENERIC_ERROR') {
    try {
        // Try to parse JSON error response
        const errorData = await response.json();

        // Backend returns { error: "ERROR_CODE", message: "...", detail: "..." }
        if (errorData.error) {
            // Translate the error code
            const translatedMessage = t(errorData.error, language);
            showError(messagesContainer, translatedMessage);
            return translatedMessage;
        } else if (errorData.message) {
            // Fallback to message if error code not present
            showError(messagesContainer, errorData.message);
            return errorData.message;
        }
    } catch (e) {
        // JSON parsing failed or no body, use fallback
        console.error('Failed to parse error response:', e);
    }

    // Ultimate fallback
    const fallbackMessage = t(fallbackMessageCode, language);
    showError(messagesContainer, fallbackMessage);
    return fallbackMessage;
}

// Initialize session ID
// Returns { sessionId, propertyId } or null on failure
export async function initializeSession(apiEndpoint) {
    try {
        const response = await fetch(apiEndpoint, {
            method: 'POST',
            headers: buildHeaders(),
            body: JSON.stringify({
                query: '__INIT_SESSION__', // Special marker that won't trigger any rules
                session_id: ''
            })
        });

        if (response.ok) {
            const data = await response.json();
            if (data.session_id) {
                saveSessionId(data.session_id);
                // Also capture property_id from response (needed for sendBeacon which can't send headers)
                if (data.property_id && !currentPropertyID) {
                    currentPropertyID = data.property_id;
                }
                return { sessionId: data.session_id, propertyId: data.property_id || null };
            }
        }
    } catch (error) {
        console.error('Failed to initialize session:', error);
    }
    return null;
}

// Send query to API
export async function sendQuery(apiEndpoint, query, sessionId, responseSizeLimit = 0, flowId = 0) {
    const requestBody = {
        query: query,
        session_id: sessionId
    };

    if (responseSizeLimit > 0) {
        requestBody.response_size_limit = responseSizeLimit;
    }

    if (flowId > 0) {
        requestBody.flow_id = flowId;
    }

    const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
}

// Submit user details
export async function submitUserDetailsAPI(baseUrl, sessionId, userDetailsData, ruleId = null) {
    // Prepare request body
    const requestBody = {
        session_id: sessionId,
        chat_history: userDetailsData.chatHistory,
        first_name: userDetailsData.firstName,
        last_name: userDetailsData.lastName,
        phone: userDetailsData.phone,
        email: userDetailsData.email,
        consent: userDetailsData.consent
    };

    // Include rule_id if it was set (from response rule)
    if (ruleId !== null && ruleId !== undefined) {
        requestBody.rule_id = ruleId;
    }

    // POST to capture endpoint
    const response = await fetch(`${baseUrl}/api/capture-user-details`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
        throw response;
    }

    return await response.json();
}

// Submit phone only
export async function submitPhoneAPI(baseUrl, sessionId, phoneData) {
    const requestBody = {
        session_id: sessionId,
        chat_history: phoneData.chatHistory,
        first_name: phoneData.firstName,
        last_name: phoneData.lastName,
        phone: phoneData.phone,
        consent: phoneData.consent
    };

    const response = await fetch(`${baseUrl}/api/capture-phone`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
        throw response;
    }

    return await response.json();
}

// Submit email only
export async function submitEmailAPI(baseUrl, sessionId, emailData) {
    const requestBody = {
        session_id: sessionId,
        chat_history: emailData.chatHistory,
        first_name: emailData.firstName,
        last_name: emailData.lastName,
        email: emailData.email,
        consent: emailData.consent
    };

    const response = await fetch(`${baseUrl}/api/capture-email`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
        throw response;
    }

    return await response.json();
}

// Submit appointment
export async function submitAppointmentAPI(baseUrl, sessionId, appointmentData, recipientEmails = null) {
    // Prepare request data
    const requestData = {
        session_id: sessionId,
        appointment_date: appointmentData.date,
        appointment_time: appointmentData.time,
        appointment_notes: appointmentData.notes
    };

    // Add user details if not already captured
    if (appointmentData.userDetails) {
        requestData.first_name = appointmentData.userDetails.firstName;
        requestData.last_name = appointmentData.userDetails.lastName;
        requestData.phone = appointmentData.userDetails.phone;
        requestData.email = appointmentData.userDetails.email;
    }

    // Include recipient emails if provided from multi-option rule
    if (recipientEmails) {
        requestData.recipient_emails = recipientEmails;
    }

    // Send to backend
    const response = await fetch(baseUrl + '/api/capture-appointment', {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify(requestData)
    });

    if (!response.ok) {
        throw response;
    }

    return await response.json();
}

// Send conversation via email
export async function sendConversationEmailAPI(baseUrl, sessionId, userDetails, recipientEmails = null) {
    // Get conversation history from session storage
    const messages = loadMessages();

    // Build conversation text for email
    let conversationText = '';
    messages.forEach(msg => {
        const role = msg.role === 'user' ? 'User' : 'Assistant';
        conversationText += `${role}: ${msg.content}\n\n`;
    });

    // Prepare request data
    const requestData = {
        session_id: sessionId,
        name: userDetails ? `${userDetails.firstName} ${userDetails.lastName}` : 'Anonymous',
        phone: userDetails ? userDetails.phone : '',
        email: userDetails ? userDetails.email : '',
        conversation: conversationText
    };

    // Include recipient emails if provided from multi-option rule
    if (recipientEmails) {
        requestData.recipient_emails = recipientEmails;
    }

    // Send to backend
    const response = await fetch(baseUrl + '/api/send-conversation-via-email', {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify(requestData)
    });

    if (!response.ok) {
        throw response;
    }

    return await response.json();
}

// Submit contact us form
export async function submitContactUsAPI(baseUrl, sessionId, contactData) {
    const requestBody = {
        session_id: sessionId,
        name: contactData.name,
        email: contactData.email,
        phone: contactData.phone,
        message: contactData.message
    };

    const response = await fetch(`${baseUrl}/api/contact-us/submit`, {
        method: 'POST',
        headers: buildHeaders(),
        body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
        throw response;
    }

    return await response.json();
}

// Save chat log (called on page close/conversation end)
// Uses fetch with keepalive for reliable delivery on page unload
// propertyID is included in the body because keepalive requests can't rely on headers
export function saveChatLogAPI(baseUrl, sessionId, endReason = 'page_close', propertyID = null) {
    if (!sessionId) {
        return;
    }

    const requestBody = {
        session_id: sessionId,
        end_reason: endReason
    };

    // Include property_id in body
    if (propertyID) {
        requestBody.property_id = propertyID;
    } else if (currentPropertyID) {
        requestBody.property_id = currentPropertyID;
    }

    const url = baseUrl ? `${baseUrl}/api/save-chat` : '/api/save-chat';

    // Use fetch with keepalive: true for reliable delivery during page unload
    // This is more reliable than sendBeacon for cross-origin requests
    try {
        fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
            body: JSON.stringify(requestBody),
            keepalive: true  // Allows request to outlive the page
        }).catch(() => {
            // Silent fail - best effort during page unload
        });
    } catch (e) {
        // Silent fail - best effort during page unload
    }
}
